import { useState } from 'react';
import type { MediaItem } from '@cornerstone/shared';
import { ModalShell } from './menu';
import { Button, Field } from './ui';
import { useCalibrationStore } from '../store/calibration';
import { useDeviceGroupsStore } from '../store/deviceGroups';
import { ContentOwnerGroupField, applyOwnerGroup, ownerGroupOf } from './ContentOwnerGroupField';
import { describeApiError, toast } from './toast';

// =============================================================================
// MediaEditModal — 라이브러리 ⋮ → '수정'. 파일 이름만.
// 옛 '콘텐츠 정보'(라이브러리 패널 안에 끼어들던 인스펙터)를 대신한다.
// 2026-09-16 대교 결정("폴더 개념 없애자")으로 폴더 선택 필드를 없앴다 — 콘텐츠는 소유 그룹으로만 갈린다.
// 공개/비공개(공개 범위)는 2026-09-17 대교 결정으로 화면에서 없앴다 — 폴더·공통 분류를 걷어낸
// 뒤로 남아 있던 개념의 잔재다. 콘텐츠가 어느 그룹 것인가는 서버가 소유 그룹으로 정한다.
// =============================================================================

export function MediaEditModal({
  item,
  canChooseGroup = false,
  onClose,
  onSaved,
}: {
  item: MediaItem;
  /** 소유 그룹을 옮길 수 있는가 — 조직 Owner·중앙 관리자만. */
  canChooseGroup?: boolean;
  onClose: () => void;
  /** 소유 그룹이 바뀌어 목록을 다시 읽어야 할 때. */
  onSaved?: (() => void | Promise<void>) | undefined;
}): JSX.Element {
  const updateMediaMeta = useCalibrationStore((s) => s.updateMediaMeta);
  const [name, setName] = useState(item.originalName);
  const groups = useDeviceGroupsStore((s) => s.groups);
  const initialGroup = ownerGroupOf(item);
  const [ownerGroupId, setOwnerGroupId] = useState(initialGroup ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError(null);
    try {
      // 소유 그룹 먼저(revision 검사), 그다음 이름. folderId는 더 이상 보내지 않는다(2026-09-16).
      const moved = canChooseGroup && initialGroup !== null
        ? await applyOwnerGroup('media', item, ownerGroupId) : false;
      const patch: { originalName?: string } = {};
      if (trimmed !== item.originalName) patch.originalName = trimmed;
      if (Object.keys(patch).length) await updateMediaMeta(item.id, patch);
      if (moved) await onSaved?.();
      toast('저장되었습니다.', 'success');
      onClose();
    } catch (e) {
      setError(describeApiError(e));
      setBusy(false);
    }
  };

  return (
    // 제출·취소는 고정 푸터로(2026-09-16 검수 P2) — 내용이 길어져도 버튼이 화면 밖으로 밀리지 않는다.
    <ModalShell
      title="미디어 수정"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button variant="primary" disabled={!canSubmit} onClick={() => void submit()}>{busy ? '저장 중…' : '저장'}</Button>
        </>
      }
    >
      <Field label="파일 이름 *" value={name} autoFocus onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
      {canChooseGroup && initialGroup !== null && (
        <ContentOwnerGroupField value={ownerGroupId} onChange={setOwnerGroupId} groups={groups} noun="미디어" />
      )}
      {error && <div className="font-mono text-[12px] text-red">{error}</div>}
    </ModalShell>
  );
}
